import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { ContentContainer } from '../components/HeroSectionStyles';
import ParticlesBackground from '../components/ParticlesBackground';

// Page transition animations
const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(-40px); }
`;

const slideOutToRight = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(40px); }
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &.enter-right { animation: ${slideInFromRight} 280ms ease-out both; }
  &.enter-left { animation: ${slideInFromLeft} 280ms ease-out both; }
  &.exit-left { animation: ${slideOutToLeft} 240ms ease-in both; }
  &.exit-right { animation: ${slideOutToRight} 240ms ease-in both; }
`;

const Content = styled(ContentContainer)`
  max-width: 1100px;
  width: 92%;
  margin: 0 auto;
  padding: 48px 56px;
  border: 1px solid rgba(100, 182, 255, 0.25);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.08);
  backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.6rem;
  color: #64B6FF;
  margin: 1.2rem 0 1.2rem;
  text-align: center;
  letter-spacing: 2px;
  text-shadow: 0 0 12px rgba(100, 182, 255, 0.5);
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: translateY(${props => (props.visible ? '0' : '6px')});
  transition: opacity 240ms ease, transform 240ms ease;
`;

const Flow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 1.4rem;
  color: rgba(255,255,255,0.92);
`;

const FlowPill = styled.span`
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid rgba(100, 182, 255, 0.35);
  background: rgba(10, 18, 28, 0.55);
  box-shadow: 0 0 10px rgba(100, 182, 255, 0.15) inset;
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: translateY(${props => (props.visible ? '0' : '6px')});
  transition: opacity 220ms ease, transform 220ms ease;
`;

const Arrow = styled.span`
  color: #64B6FF;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.6);
  opacity: ${props => (props.visible ? 1 : 0)};
  transform: translateY(${props => (props.visible ? '0' : '6px')});
  transition: opacity 220ms ease, transform 220ms ease;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.4);
  background: rgba(10, 18, 28, 0.6);
  color: #64B6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0 12px rgba(100, 182, 255, 0.25);
  backdrop-filter: blur(4px);
  transition: transform 120ms ease, box-shadow 200ms ease, background 200ms ease;

  &:hover {
    transform: translateX(-2px);
    box-shadow: 0 0 16px rgba(100, 182, 255, 0.45);
    background: rgba(10, 18, 28, 0.75);
  }
`;

const NextButton = styled(BackButton)`
  left: auto;
  right: 20px;

  &:hover {
    transform: translateX(2px);
  }
`;

function ReuniaoOnboardingFormatosIntro() {
  const location = useLocation();
  const navigate = useNavigate();

  const [anim, setAnim] = useState(() => (location.state?.dir === 'back' ? 'enter-left' : 'enter-right'));
  const [step, setStep] = useState(0); // 0: nada; 1: título; 2: headline; 3: vídeo; 4: ligação

  const goBack = () => {
    setAnim('exit-right');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/objetivos${location.search}`, { state: { dir: 'back' } });
    }, 240);
  };

  const goNext = () => {
    setAnim('exit-left');
    setTimeout(() => {
      navigate(`/reuniao-onboarding/formatos/video${location.search}`, { state: { dir: 'forward' } });
    }, 240);
  };

  const advance = () => {
    setStep((prev) => {
      if (prev < 4) return prev + 1;
      // último passo já visível, avança para a próxima página
      goNext();
      return prev;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goBack();
      else if (e.key === 'ArrowRight') advance();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.search]);

  return (
    <>
      <ParticlesBackground />
      <PageWrapper className={anim} onClick={advance}>
        <BackButton onClick={(e) => { e.stopPropagation(); goBack(); }} aria-label="Voltar">‹</BackButton>
        <NextButton onClick={(e) => { e.stopPropagation(); advance(); }} aria-label="Avançar">›</NextButton>
        <Content>
          <SectionTitle visible={step >= 1}>Formato de Anúncio</SectionTitle>
          <Flow>
            <FlowPill visible={step >= 2}>Headline</FlowPill>
            <Arrow visible={step >= 3}>›</Arrow>
            <FlowPill visible={step >= 3}>Vídeo do imóvel</FlowPill>
            <Arrow visible={step >= 4}>›</Arrow>
            <FlowPill visible={step >= 4}>Oferece Ligação</FlowPill>
          </Flow>
        </Content>
      </PageWrapper>
    </>
  );
}

export default ReuniaoOnboardingFormatosIntro;
